import React from 'react';
import { ArrowRightIcon, CornerDownRightIcon } from 'lucide-react';
import { lateChanges } from '../../data/periodClose';
import { carriedAdjustments } from '../../data/statement';
import { Badge } from '../sf/Badge';
import { Money } from '../sf/Money';
import { NewBadge } from '../sf/NewBadge';
import { StatusBadge } from '../sf/StatusBadge';
import { PaidTotalCallout } from './PaidTotalCallout';
const months = [{
  name: 'September 2026',
  badge: <StatusBadge status="Locked" data-id="element-340" data-mp="ot8xi0zgxxno" />,
  note: 'Paid and read-only (August and earlier too).',
  current: false
}, {
  name: 'October 2026',
  badge: <Badge tone="info" data-id="element-341" data-mp="fzdryrufq57i">Open → being closed now</Badge>,
  note: 'It is being locked. Its total freezes at lock.',
  current: true
}, {
  name: 'November 2026',
  badge: <Badge tone="neutral" data-id="element-342" data-mp="jymi7mq9nkyb">Open next</Badge>,
  note: 'Receives any change made after October locks.',
  current: false
}];
export function LockVisual() {
  const afterLock = lateChanges[0];
  return <div className="space-y-4" data-id="element-343" data-mp="1ciuwanbxiav5">
      <PaidTotalCallout data-id="element-344" data-mp="36iakrqs732y6" />
      <p className="flex flex-wrap items-center gap-2 text-xs text-sf-weak" data-id="element-345" data-mp="36jlpbstgseqc">
        <NewBadge data-id="element-346" data-mp="31jzchkhjt0i2" />
        Period locking and adjustments posted to the next open period are proposed. Today a post-funding loan edit silently rewrites the original month’s fee.
      </p>
      <ol className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_1fr_auto_1fr]" data-id="element-347" data-mp="3bhn5a4vlfxxl">
        {months.map((month, index) => <React.Fragment key={month.name} data-id="element-348" data-mp="3p8a6c3ottzwu" data-mpi={index}>
            <li className={`rounded border px-3 py-2 ${month.current ? 'border-sf-blue bg-sf-blue-light' : 'border-sf-border bg-white'}`} data-id="element-349" data-mp="3pb4sbznhnssd">
              <p className="flex flex-wrap items-center justify-between gap-2 text-[13px] font-semibold" data-id="element-350" data-mp="32dalhu19uzu">
                {month.name} {month.badge}
              </p>
              <p className="mt-0.5 text-xs text-sf-weak" data-id="element-351" data-mp="3u6bp0nicbx7s">{month.note}</p>
            </li>
            {index < months.length - 1 && <li className="hidden items-center text-sf-weak md:flex" aria-hidden="true" data-id="element-352" data-mp="1zun506x954gj">
                <ArrowRightIcon className="h-4 w-4" data-id="element-353" data-mp="3rigp05fv4aq" />
              </li>}
          </React.Fragment>)}
      </ol>

      <section aria-label="Adjustments posted into October" data-id="element-354" data-mp="8qv1zggnw3b7">
        <h3 className="text-[13px] font-semibold" data-id="element-355" data-mp="crx362u52pft">Before October locks</h3>
        <p className="mb-1.5 text-xs text-sf-weak" data-id="element-356" data-mp="cu04vk4nm70d">Adjustments from changes to locked months (Aug / Sep), posted into October</p>
        <ul className="space-y-1.5" data-id="element-357" data-mp="mqcvg94wk4qw">
          {carriedAdjustments.map(item => <li key={item.id} className="flex flex-wrap items-center gap-2 text-[13px]" data-id="element-358" data-mp="roxu444iagd8">
              <CornerDownRightIcon className="h-4 w-4 text-sf-weak" aria-hidden="true" data-id="element-359" data-mp="wfmobr7klrg2" />
              <span data-id="element-360" data-mp="wi0hfuiowtkz">
                {item.loanNumber} changed after <strong data-id="element-361" data-mp="10g6luvw22x2q">{item.sourcePeriod}</strong> locked
              </span>
              <span className="font-semibold" data-id="element-362" data-mp="15gul24ufuk81">
                <Money value={item.amount} signed data-id="element-363" data-mp="15i5o22g27ny3" />
              </span>
              <span className="text-sf-weak" data-id="element-364" data-mp="1aggyaea9o0gu">→ Adjustment line in October 2026 · {item.sourcePeriod} untouched</span>
            </li>)}
        </ul>
      </section>

      <section aria-label="Change after October locks" className="border-t border-sf-border pt-3" data-id="element-365" data-mp="1fjq6d8llsidz">
        <h3 className="text-[13px] font-semibold" data-id="element-366" data-mp="1akmylge8ny2b">After October locks</h3>
        <p className="mb-1.5 text-xs text-sf-weak" data-id="element-367" data-mp="1amzp4hy2bgqz">October’s total stays exactly as it was at the moment of lock</p>
        <div className="flex flex-wrap items-center gap-2 text-[13px]" data-id="element-368" data-mp="239kg3hamlqu0">
          <CornerDownRightIcon className="h-4 w-4 text-sf-weak" aria-hidden="true" data-id="element-369" data-mp="23bydvxwoxh59" />
          <span data-id="element-370" data-mp="1ybixsyl0xvjl">
            {afterLock.loanNumber} · {afterLock.field} {afterLock.from} → {afterLock.to}
          </span>
          <span className="font-semibold" data-id="element-371" data-mp="1tho9fhuh4se6">
            <Money value={afterLock.estImpact} signed data-id="element-372" data-mp="1phe2o2yuxxy9" />
          </span>
          <span className="text-sf-weak" data-id="element-373" data-mp="2m03benmr85vm">→ Adjustment line in November 2026 · October untouched</span>
        </div>
        <p className="ml-6 mt-0.5 text-xs tabular-nums text-sf-weak" data-id="element-374" data-mp="2h9d3p3quvqxr">{afterLock.impactDetail}</p>
      </section>
    </div>;
}